<template>
<div
  :class="[
    'extra-bonus-line',
    {'extra-bonus-line__attention': reset}
  ]"
  :style="{'--progress-result-color': color}"
  :data-period="currentPeriod"
>
  <div
    :class="[
      'extra-bonus-line_info',
      {'extra-bonus-line_info-mounted': mounted},
    ]"
  >
    <div class="hgroup">
      <div class="heading1">{{ $c('Экстра награды') }}</div>
      <a
        href="#"
        class="title-info"
        @click.prevent="showMoreDialog = true"
      >{{ $c('Узнать больше о программе') }}</a>
      <Portal to="overlay" v-if="showMoreDialog">
        <PopUp
          :pop-up-component="ExtraBonusMore"
          :pop-up-props="{game, partner}"
          pop-up-valign="top"
          @closePopUp="showMoreDialog = false"
        />
      </Portal>
    </div>
    <div class="extra-bonus-line_description">
      <span><span v-if="!allPeriodsComplete">Следующая награда </span><b>“{{ header }}”</b>.</span>
      <div v-html="description" />
    </div>
    <ExtraBonusIntersectionObserver
      :reset="reset"
      :card="card"
      :stateId="stateId"
    >
      <div class="extra-bonus-line_progress">
        <ul class="extra-bonus-line_progress-list">
          <li
            v-for="({month, period, firstInPeriod, lastInPeriod}, index) of monthRow"
            v-bind:key="`month_${index}`"
            :class="[
              'extra-bonus-line_progress-month',
              'extra-bonus-line_progress-list-item',
              {'extra-bonus-line_progress-month__success': progressMonth >= index},
              {'extra-bonus-line_progress-month__current': progressMonth === index},
              {'extra-bonus-line_progress-month__first-in-period': firstInPeriod},
            ]"
            :data-period="period"
            :data-last-in-period="lastInPeriod"
            ref="month"
          >
            <span
              v-if="index === 0"
              class="extra-bonus-line_progress-list-item-text"
            ><span>0</span></span>
            <span
              v-if="firstInPeriod && currentPeriod > 0"
              class="extra-bonus-line_progress-list-item-text extra-bonus-line_progress-list-item-text__mobile"
            ><span>{{ month - 1 }}</span></span>
            <span class="extra-bonus-line_progress-list-item-text">
              <span>{{ month }}</span>
            </span>
            <span
              v-if="period === currentPeriod && index <= progressMonth"
              class="extra-bonus-line_current-period"
              :style="{'--period-progress': `${((progress + 1) / month) * 100}%`}"
            >
              <!-- Progress month line -->
              <span
                class="extra-bonus-line_current-month"
                v-if="currentPeriodStatus === PERIOD_STATUS.WAITING && progressMonth === index"
                :style="{'--current-period-month-width': `${(100 / (progress + 1))}%`}"
              ></span>
            </span>
            <span
              v-if="lastInPeriod"
              class="extra-bonus-line_progress-present"
            >
              <svg
                width="15"
                height="14"
                viewBox="0 0 15 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M1.5 3L7.5 6L13.5 3L7.5 0L1.5 3ZM14.5 5L8.5 8V14L14.5 11V5ZM6.5 8L0.5 5V11L6.5 14V8Z"
                  :fill="tooltipStatuses[period] === PERIOD_STATUS.ACCRUED ? color : '#999999'"
                />
              </svg>
              <ExtrabonusTooltip
                :title="tooltipHeaders[period]"
                :content="tooltipDescriptions[period]"
                :received="tooltipStatuses[period] === PERIOD_STATUS.ACCRUED"
                position="top"
              />
            </span>
            <span
              v-if="firstInPeriod && currentPeriod > 0"
              class="extra-bonus-line_progress-present extra-bonus-line_progress-present__mobile"
            >
              <svg
                width="15"
                height="14"
                viewBox="0 0 15 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M1.5 3L7.5 6L13.5 3L7.5 0L1.5 3ZM14.5 5L8.5 8V14L14.5 11V5ZM6.5 8L0.5 5V11L6.5 14V8Z"
                  :fill="color"
                />
              </svg>
              <ExtrabonusTooltip
                v-if="period > 0"
                :title="tooltipHeaders[period - 1]"
                :content="tooltipDescriptions[period - 1]"
                :received="true"
                position="top"
              />
            </span>
          </li>
        </ul>
        <div class="extra-bonus-line_mobile-tooltip">
          <ExtrabonusTooltip
            :title="tooltipHeaders[currentPeriod]"
            :content="tooltipDescriptions[currentPeriod]"
            :received="tooltipStatuses[currentPeriod] === PERIOD_STATUS.ACCRUED"
            position="top"
          />
        </div>
      </div>
    </ExtraBonusIntersectionObserver>
    <div :class="[
      'extra-bonus-line_goldback',
      {'extra-bonus-line_goldback__error': reset}
    ]">
      <span>{{ $c('Голдбек:') }}</span>
      <GameIcon :game="game" />
      <span>{{extraBonus.lastGoldback}} / {{ totalGoldBackGoal }}</span>
    </div>
    <template v-if="firstPeriodAndMonth">
      <div
        class="extra-bonus-line_goldback-description"
        v-if="!reset"
      >
        {{ $c('Подсчитываем голдбек за 1-й месяц 1-го этапа...') }}
      </div>
    </template>
    <template v-else>
      <div
        class="extra-bonus-line_goldback-description"
        v-if="!reset"
        v-html="$c(successDescription, {month: renderTextMonth, period: renderTextPeriod})"
      />
    </template>
    <div
      class="extra-bonus-line_goldback-description extra-bonus-line_goldback-description__error"
      v-if="reset"
      v-html="$c(failureDescription, {month: renderTextMonth, period: renderTextPeriod})"
    />
    <div class="extra-bonus-line_goldback-info">Всего программа длится 12 месяцев. За это время вы можете получить 4 набора наград, по одной за каждый трехмесячный этап программы.</div>
  </div>
</div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator'
import ExtrabonusTooltip from './ExtrabonusTooltip'
import PopUp from '@/components/PopUp/PopUp'
import ExtraBonusMore from '@/components/ExtraBonus/ExtraBonusMore'
import ExtraBonusIntersectionObserver from '@/components/ExtraBonus/ExtraBonusIntersectionObserver'
import GameIcon from './GameIcon'
import { GAME_TO_COLORS } from '@/constants'

const PERIOD_STATUS = {
  ACCRUED: 'ACCRUED',
  WAITING: 'WAITING'
}

@Component({
  components: {
    ExtrabonusTooltip,
    PopUp,
    ExtraBonusMore,
    GameIcon,
    ExtraBonusIntersectionObserver
  }
})
class ExtraBonusLine extends Vue {
  @Prop({
    required: true,
    type: String
  }) game

  @Prop({
    required: true,
    type: Object
  }) card

  @Prop({
    required: true,
    type: String
  }) partner

  @Prop({
    required: true,
    type: Object
  }) extraBonus

  PERIOD_STATUS = PERIOD_STATUS

  showMoreDialog = false
  ExtraBonusMore = ExtraBonusMore

  get reset () {
    return this.extraBonus.reset
  }

  get progress () {
    return this.extraBonus.progress
  }

  get period () {
    return this.extraBonus.period
  }

  ALL_PERIODS = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    [10, 11, 12]
  ]

  mounted = false

  get currentPeriod () {
    return this.period
  }

  get color () {
    return GAME_TO_COLORS[this.game]
  }

  get successDescription () {
    try {
      return this.extraBonus.periods[this.currentPeriod].successDescription
    } catch (e) {
      return ''
    }
  }

  get failureDescription () {
    try {
      return this.extraBonus.periods[this.currentPeriod].failureDescription
    } catch (e) {
      return ''
    }
  }

  get currentPeriodStatus () {
    try {
      return this.extraBonus.periods[this.currentPeriod].status
    } catch (e) {
      return ''
    }
  }

  get stateId () {
    return this.extraBonus.stateId
  }

  get progressMonth () {
    return this.ALL_PERIODS[this.period][this.progress] - 1
  }

  get monthRow () {
    return this.ALL_PERIODS.reduce((item, acc, period) => {
      return [
        ...item,
        ...acc.map((month, index) => {
          return {
            month,
            period,
            lastInPeriod: !(acc.length - 1 > index),
            firstInPeriod: index === 0
          }
        })
      ]
    }, [])
  }

  get currentPeriodBonuses () {
    if (this.period === undefined) return {}
    return this.extraBonus.periods.find(item => item.period === this.period)
  }

  get header () {
    return this.currentPeriodBonuses.mainHeader
  }

  get description () {
    return this.currentPeriodBonuses.mainDescription
  }

  get status () {
    return this.currentPeriodBonuses.status
  }

  get tooltipHeaders () {
    return this.extraBonus.periods.map(item => item.tooltipHeader)
  }

  get tooltipDescriptions () {
    return this.extraBonus.periods.map(item => item.tooltipDescription)
  }

  get tooltipStatuses () {
    return this.extraBonus.periods.map(item => item.status)
  }

  get totalGoldBackGoal () {
    // return this.extraBonus.periods.reduce((acc, obj) => (acc + obj.goldbackGoal), 0)
    return this.extraBonus.periods[this.currentPeriod].goldbackGoal
  }

  get firstPeriodAndMonth () {
    return (this.period === 0 && this.progress === 0)
  }

  get renderTextPeriod () {
    if (this.period > 0 && this.progress === 0 && !this.reset) {
      return this.period
    }
    return this.period + 1 <= this.ALL_PERIODS.length ? this.period + 1 : this.period
  }

  get renderTextMonth () {
    if (this.reset) {
      return this.extraBonus.progress + 1
    }
    if (this.period > 0 && this.progress === 0 && !this.reset) {
      return this.ALL_PERIODS[this.period].length
    }
    return this.extraBonus.progress
  }

  get allPeriodsComplete () {
    return this.extraBonus.periods[this.extraBonus.periods.length - 1].status === PERIOD_STATUS.ACCRUED
  }

  mounted () {
    setTimeout(() => {
      this.mounted = true
    }, 100)
  }
}

export default ExtraBonusLine

</script>

<style lang="scss">
  .extra-bonus-line {
    $grid-half-gap: 20px;
    @extend %layout;
    margin-bottom: 60px;
    .hgroup {
      @include media_desktop {
        margin-bottom: 24px;
      }
    }
    .title-info {
      &:hover {
        color: #ffffff;
      }
    }
    &_description {
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 22px;
      color: #999;
      margin-bottom: 24px;
      br:not(.line) {
        display: none;
      }
    }
    &_progress {
      display: block;
      position: relative;
    }
    &_current-period {
      display: block;
      position: absolute;
      right: 1px;
      bottom: 0;
      height: 10px;
      width: calc(var(--period-progress) - 1px);
      max-width: 100%;
      .extra-bonus-line__attention & {
        background-color: #FF002A;
        z-index: 1;
      }
    }
    &_current-month {
      width: var(--current-period-month-width);
      height: 100%;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 16;
      overflow: hidden;
      .extra-bonus-line__attention & {
        display: none;
      }
      &:after {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        width: 200px;
        height: 100%;
        background: url("./img/mask.png") 0 0 repeat-x;
        animation: move 20s infinite linear;
      }
    }
    &_progress-result {
      height: 10px;
      width: var(--progress-result-width);
      background-color: var(--progress-result-color);
      position: absolute;
      bottom: 0;
    }
    &_progress-present {
      position: absolute;
      right: 1px;
      bottom: -24px;
      transform: translateX(50%);
      height: 14px;
      display: flex;
      flex-direction: column;
      &__mobile {
        display: none;
        @include media_mobile_only {
          left: -14px;
          right: auto;
          display: flex;
        }
      }
      .extrabonus-tooltip {
        opacity: 0;
        pointer-events: none;
        transition: opacity .3s ease;
      }
      svg path {
        transition: fill .3s ease;
      }
      &:hover {
        @include media_tablet {
          svg {
            cursor: pointer;
            filter: drop-shadow(0 0 10px #fff);
            path {
              fill: #ffffff;
            }
          }
          .extrabonus-tooltip {
            @include media_tablet {
              opacity: 1;
            }
          }
        }
      }
    }
    &_progress-list {
      display: block;
      width: 100%;
      margin-bottom: 24px;
      &:after {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 10px;
        background-color: #000000;
      }
    }
    &_progress-list-item {
      display: flex;
      justify-content: flex-end;
      font-style: normal;
      font-weight: normal;
      color: #666666;
      padding-bottom: 14px;
      position: absolute;
      left: 0;
      top: 0;
      $month: 1;
      $total-months: 12;
      @while($month <= $total-months) {
        &:nth-child(#{$month}) {
          width: calc($month / $total-months * 100%);
          z-index: $total-months + 1 - $month;
        }
        $month: $month + 1;
      }
      &:first-child {
        position: relative;
        justify-content: space-between;
      }
      &:after {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        bottom: 0;
        width: 0;
        height: 10px;
        background-color: #000000;
        transition: width .3s ease;
        .extra-bonus-line_info-mounted & {
          width: 100%;
        }
      }
    }
    &_progress-month {
      &__success {
        &[data-last-in-period] {
          font-weight: bold;
          font-size: 16px;
          line-height: 16px;
          color: #ffffff;
        }
        svg {
          filter: drop-shadow(0 0 10px var(--progress-result-color));
        }
        &:after {
          background-color: var(--progress-result-color);
        }
      }

      &__first-in-period {
        @include media_mobile_only {
          justify-content: space-between;
        }
      }
    }
    &_progress-list-item-text {
      position: relative;
      width: 2px;
      text-align: center;
      &__mobile {
        display: none;
        @include media_mobile_only {
          display: inline;
        }
      }
      &:after {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        top: calc(100% + 4px);
        height: 20px;
        width: 2px;
        background-color: #333;
        z-index: 15;
      }
      > span {
        position: relative;
        left: 50%;
        transform: translateX(-50%);
        display: block;
        text-align: center;
        font-size: 12px;
        line-height: 12px;
        width: 10px;
      }
    }
    &_goldback {
      color: var(--progress-result-color);
      font-style: normal;
      font-weight: bold;
      font-size: 16px;
      line-height: 24px;
      display: flex;
      align-items: center;
      padding-top: 28px;
      svg {
        display: block;
        margin: 0 8px;
        path {
          fill: var(--progress-result-color);
        }
      }
      &__error {
        color: #ff002a;
        svg {
          path {
            fill: #ff002a;
          }
        }
      }
    }
    &_goldback-progress {
      margin-top: 13px;
      color: var(--progress-result-color);
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 16px;
      @include media_tablet {
        font-size: 14px;
        line-height: 22px;
      }
    }
    &_goldback-info {
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 16px;
      margin-top: 8px;
      @include media_tablet {
        font-size: 14px;
        line-height: 22px;
      }
    }
    &_mobile-tooltip {
      position: absolute;
      left: 50%;
      bottom: -20px;
      opacity: 0;
      pointer-events: none;
      transition: opacity .3s ease;
      .extra-bonus-line_progress:hover & {
        opacity: 1;
        @include media_tablet {
          opacity: 0;
        }
      }
    }
    &_goldback-description {
      margin-top: 8px;
      color: var(--progress-result-color);
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 16px;
      @include media_tablet {
        font-size: 14px;
        line-height: 22px;
      }
      &__error {
        color: #ff002a;
      }
      @include media_desktop {
        width: 50%;
      }
    }
    $period: 0;
    $total-period: 3;
    @while($period <= $total-period) {
      @include media_mobile_only {
        &[data-period="#{$period}"] {
          .extra-bonus-line_current-period {
            width: 100%;
          }
          .extra-bonus-line_current-month {
            width: 100%;
          }
          .extra-bonus-line_progress-month {
            width: 100%;
            position: relative;
            &:not([data-period="#{$period}"]) {
              display: none;
            }
          }
        }
      }
      $period: $period + 1;
    }
  }
  @include media_mobile_only {
    .extra-bonus-line_progress-list {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
    }
  }
  @keyframes move {
      from {transform: translateX(  0%);}
        to {transform: translateX(-50%);}
  }
</style>
